import { AddressDisplay } from '@liveauctioneers/hammer-ui-core/addressDisplay';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { SubtleLink } from '@liveauctioneers/hammer-ui-core/link';
import { Radio } from '@liveauctioneers/hammer-ui-core/radio';
import { LabelMini } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import { getFormattedPhoneNumber, getNationalPhoneNumberForm } from '@/components/Forms/utils/phoneForm';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getUpdateAccountLoading } from '@/redux/modules/account/updateAccount/updateAccount';
import { updateBidderAddress } from '@/redux/modules/account/updateAccount/updateAccount.actions';
import { isUserDataLoading } from '@/redux/modules/account/user/user.selectors';
import { openAddressModal } from '@/redux/modules/modal';
import { BidderAddress } from '@/types/BidderAddresses';

type Props = {
    address: BidderAddress;
    canEdit: boolean;
    onSelect: (address: BidderAddress) => void;
    selected: boolean;
    selectedId: number;
    showRemoveButton: boolean;
    showSetDefaultButton?: boolean;
};

const BidderAddressListItemComponent = ({
    address,
    canEdit,
    onSelect,
    selected,
    showRemoveButton,
    showSetDefaultButton,
}: Props) => {
    const dispatch = useAppDispatch();

    const updateAccountLoading = useAppSelector(getUpdateAccountLoading);
    const userDataLoading = useAppSelector(isUserDataLoading);

    const openEditModal = (address: BidderAddress, id: number) => {
        dispatch(openAddressModal({ address, addressID: id }));
    };
    const removeAddress = (address: BidderAddress) => {
        dispatch(updateBidderAddress(address.bidderId, { ...address, enabled: false }, ''));
    };

    const formattedPhoneNumber = getNationalPhoneNumberForm(address.phoneNumber);
    const phoneNumber =
        formattedPhoneNumber.countryCode === 'US' || formattedPhoneNumber.countryCode === 'CA'
            ? getFormattedPhoneNumber(address.phoneNumber)
            : formattedPhoneNumber.phoneNumber;

    const addressComponent = (
        <div className="flex">
            <div className="flex flex-col">
                {address.isDefault && (
                    <LabelMini className="mb-2xs inline-block">
                        <FormattedMessage id="default" />
                    </LabelMini>
                )}
                <AddressDisplay
                    address={address}
                    className="mb-0"
                    testid="address"
                />
            </div>
        </div>
    );

    return (
        <div
            className="mb-md"
            data-testid="addressListItem"
        >
            {showSetDefaultButton ? (
                <div className="flex">
                    <div className="flex flex-col">
                        {address.isDefault && (
                            <LabelMini className="mb-2xs inline-block">
                                <FormattedMessage id="default" />
                            </LabelMini>
                        )}
                        <AddressDisplay
                            address={{ ...address, phoneNumber }}
                            className="mb-0"
                            testid="address"
                        />
                    </div>
                </div>
            ) : (
                <Radio.Option
                    checked={selected}
                    className="items-start pl-[26px] [&_input]:mt-[3px]"
                    disabled={updateAccountLoading || userDataLoading}
                    name="bidderAddress"
                    onChange={() => {
                        if (!selected) {
                            onSelect(address);
                        }
                    }}
                    value={String(address.addressId)}
                >
                    {addressComponent}
                </Radio.Option>
            )}
            {canEdit ? (
                <div className={cn('mt-2xs', !showSetDefaultButton && 'ml-lg')}>
                    <SubtleLink
                        className="underline"
                        onClick={() => openEditModal(address, address.addressId)}
                    >
                        <FormattedMessage id="addresses.edit" />
                    </SubtleLink>
                    {showRemoveButton && !address.isDefault && (
                        <SubtleLink
                            className="ml-sm underline"
                            onClick={() => removeAddress(address)}
                        >
                            <FormattedMessage id="addresses.remove" />
                        </SubtleLink>
                    )}
                    {!address.isDefault && showSetDefaultButton && (
                        <SubtleLink
                            className="ml-sm underline"
                            onClick={() => onSelect(address)}
                        >
                            <FormattedMessage id="addresses.setDefault" />
                        </SubtleLink>
                    )}
                </div>
            ) : (
                <span></span>
            )}
        </div>
    );
};

export const BidderAddressListItem = BidderAddressListItemComponent;
export default BidderAddressListItem;
